/**
 * useGalleryState Hook
 *
 * Manages the global state for the gallery component including:
 * - Navigation state
 * - Asset display state
 * - Modal states
 * - Loading states
 * - Integration with reducer logic
 * - State persistence via cache management
 */

import { useCallback, useEffect, useReducer, useMemo } from 'react'
import { reducer } from '../reducer'
import { useCacheManagement } from './useCacheManagement'

export function useGalleryState() {
  // Initial state definition
  const initialState = useMemo(
    () => ({
      navigation: [],
      assets: [],
      showAssets: false,
      rawAssets: {},
      signedAssets: {},
      publishedAssets: {},
      modalActive: false,
      modalData: {},
      modalStatus: 'Image',
      selectedCategories: [],
      documents: [],
      loading: {
        limitSize: 10,
        canLoadMore: true,
        currentPage: 1,
        isLoading: false
      }
    }),
    []
  )

  // useReducer setup
  const [state, dispatch] = useReducer(reducer, initialState)

  // Use the dedicated cache management hook
  const { saveStateToCache, loadStateFromCache, invalidateAllCache } =
    useCacheManagement()

  // Load cached state on initialization
  useEffect(() => {
    const cachedState = loadStateFromCache()
    if (cachedState && cachedState.navigation) {
      const mergedNavigation = [
        ...initialState.navigation,
        ...cachedState.navigation.filter(
          cachedCategory =>
            !initialState.navigation.some(
              initialCategory => initialCategory.id === cachedCategory.id
            )
        )
      ]

      const mergedState = {
        ...initialState,
        navigation: mergedNavigation,
        selectedCategories: cachedState.selectedCategories || [],
        showAssets: cachedState.showAssets || false
      }

      dispatch({ type: 'restore_state', payload: mergedState })
    } else {
      console.log('No cached state found. Using initial state.')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const navigationString = useMemo(
    () => JSON.stringify(state.navigation),
    [state.navigation]
  )

  const selectedCategoriesString = useMemo(
    () => JSON.stringify(state.selectedCategories),
    [state.selectedCategories]
  )

  const cacheData = useMemo(
    () => ({
      navigation: state.navigation,
      selectedCategories: state.selectedCategories,
      showAssets: state.showAssets
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigationString, selectedCategoriesString, state.showAssets]
  )

  useEffect(() => {
    if (state.navigation.length === 0 && state.selectedCategories.length === 0) {
      return
    }

    saveStateToCache(cacheData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheData, saveStateToCache])

  // Computed values based on state
  const combinedNavigation = useMemo(() => {
    if (state.selectedCategories.length === 0 && state.documents.length) {
      const groupDocsByYearName = (documents = []) =>
        documents.reduce((acc, doc) => {
          const yearCat = (doc.categories || []).find(
            c => c && c.type === 'year' && c.name
          )
          const yearName = yearCat?.name?.toString().trim() || 'Uncategorized'
          if (!acc[yearName]) acc[yearName] = []
          acc[yearName].push({
            id: doc.id,
            name: doc.origFilename || doc.name || doc.filename || 'Untitled',
            type: 'document',
            fileUrl: doc.fileUrl,
            thumbnailUrl: doc.thumbnailUrl,
            categories: doc.categories || []
          })
          return acc
        }, {})

      // Sort year keys (numeric years descending when applicable)
      const compareByName = (a, b) => {
        const an = Number(a), bn = Number(b)
        const aNum = !Number.isNaN(an), bNum = !Number.isNaN(bn)
        if (aNum && bNum) return bn - an
        return String(a).localeCompare(String(b), undefined, { numeric: true })
      }

      const grouped = groupDocsByYearName(state.documents)
      const sortedYearNames = Object.keys(grouped).sort(compareByName)

      const yearCategories = sortedYearNames.map(yearName => ({
        id: `year-${yearName}`,
        name: yearName,
        type: 'year',
        subCategories: grouped[yearName]
      }))

      return [
        ...state.navigation,
        { name: 'Documents', subCategories: yearCategories }
      ]
    }

    return state.navigation
  }, [state.navigation, state.documents, state.selectedCategories])

  // Helper functions for common state operations
  const resetState = useCallback(
    userId => {
      const cachedState = loadStateFromCache()
      if (cachedState && cachedState.selectedCategories?.length > 0) {
        // Preserve navigation position on refresh
        dispatch({
          type: 'reset_state',
          payload: {
            ...initialState,
            selectedCategories: cachedState.selectedCategories,
            showAssets: cachedState.showAssets || false
          }
        })
      } else {
        invalidateAllCache()
        dispatch({ type: 'reset_state', payload: initialState })
      }
    },
    [initialState, invalidateAllCache, loadStateFromCache]
  )

  const setNavigation = useCallback(navigationData => {
    dispatch({ type: 'set_navigation', payload: navigationData })
  }, [])

  const setDocuments = useCallback(documents => {
    dispatch({ type: 'set_documents', payload: documents })
  }, [])

  const appendSelectedCategory = useCallback(category => {
    dispatch({ type: 'append_selected_category', payload: category })
  }, [])

  const popSelectedCategory = useCallback(() => {
    dispatch({ type: 'pop_selected_category' })
    saveStateToCache(state)
  }, [saveStateToCache, state])

  const resetSelectedCategory = useCallback(() => {
    dispatch({ type: 'reset_selected_category' })
    saveStateToCache(state)
  }, [saveStateToCache, state])

  const setAssets = useCallback(assets => {
    dispatch({ type: 'set_assets', payload: assets })
  }, [])

  const appendAssets = useCallback(assets => {
    dispatch({ type: 'append_assets', payload: assets })
  }, [])

  const setRawAssets = useCallback(assets => {
    dispatch({ type: 'set_raw_assets', payload: assets })
  }, [])

  const appendRawAssets = useCallback(assets => {
    dispatch({ type: 'append_raw_assets', payload: assets })
  }, [])

  const setSignedAssets = useCallback(assets => {
    dispatch({ type: 'set_signed_assets', payload: assets })
  }, [])

  const appendSignedAssets = useCallback(assets => {
    dispatch({ type: 'append_signed_assets', payload: assets })
  }, [])

  const setPublishedAssets = useCallback(assets => {
    dispatch({ type: 'set_published_assets', payload: assets })
  }, [])

  const appendPublishedAssets = useCallback(assets => {
    dispatch({ type: 'append_published_assets', payload: assets })
  }, [])

  const setModalActive = useCallback(isActive => {
    dispatch({ type: 'set_modal_active', payload: isActive })
  }, [])

  const setModalStatus = useCallback(status => {
    dispatch({ type: 'set_modal_status', payload: status })
  }, [])

  const setModalData = useCallback(data => {
    dispatch({ type: 'set_modal_data', payload: data })
  }, [])

  const setLoading = useCallback(loadingState => {
    dispatch({ type: 'set_loading', payload: loadingState })
  }, [])

  const setShowAssets = useCallback(showAssets => {
    dispatch({ type: 'set_show_assets', payload: showAssets })
  }, [])

  const addTag = useCallback((currentIdx, newTags) => {
    dispatch({ type: 'add_tag', payload: { currentIdx, newTags } })
  }, [])

  const removeTag = useCallback((currentIdx, newTags) => {
    dispatch({ type: 'remove_tag', payload: { currentIdx, newTags } })
  }, [])

  return {
    // State
    state,
    combinedNavigation,
    initialState,

    // Raw dispatch (for compatibility during migration)
    dispatch,

    // State action functions
    resetState,
    setNavigation,
    setDocuments,
    appendSelectedCategory,
    popSelectedCategory,
    resetSelectedCategory,
    setAssets,
    appendAssets,
    setRawAssets,
    appendRawAssets,
    setSignedAssets,
    appendSignedAssets,
    setPublishedAssets,
    appendPublishedAssets,
    setModalActive,
    setModalStatus,
    setModalData,
    setLoading,
    setShowAssets,
    addTag,
    removeTag
  }
}

export default useGalleryState
